import { useForm } from "react-hook-form";
import type { Error as Auth0Error } from "@auth0/auth0-acul-js";

import { butterfieldLogo } from "@/assets/logos";
import { useMfaChallengeManager } from "../hooks/useMfaChallengeManager";
import { getFieldError } from "../utils/helpers/errorUtils";

// const heroImageUrl =
//   "https://media.butterfield.com/page-image_imageUrl_a470c5db-2106-4964-a8f2-414c0415efe6_1744290400564.jpg";
const heroImageUrl =
  "https://cdn.mediavalet.com/cato/butterfield/aEeF6_P6lk6o6VqdMg_3cQ/GX_bog2pTkawcjy_quukQA/Large/DSC09355-slovenia-ivan-sardi-2026.jpeg?f=auto";

interface MfaChallengeFormData {
  code: string;
  rememberDevice?: boolean;
}

function MfaChallengeScreen() {
  const {
    handleContinue,
    handleTryAnotherMethod,
    errors,
    texts,
    showRememberDevice,
  } = useMfaChallengeManager();

  const form = useForm<MfaChallengeFormData>({
    defaultValues: {
      code: "",
      rememberDevice: false,
    },
  });

  const {
    register,
    handleSubmit,
    formState: { errors: formErrors, isSubmitting },
  } = form;

  const sdkErrors = (errors || []) as Auth0Error[];
  const generalErrors = sdkErrors.filter((err) => !err.field);
  const codeSDKError = getFieldError("code", sdkErrors);

  const headingText = texts?.title || "Verify Your Identity";
  const descriptionText =
    texts?.description || "Enter the 6-digit code from your authenticator app.";
  const buttonText = texts?.buttonText || "Continue";
  const codePlaceholder = texts?.placeholder || "Enter the code";
  const rememberMeText =
    texts?.rememberMeText || "Remember this device for 30 days";
  const tryAnotherMethodText =
    texts?.tryAnotherMethodText || "Try another method";

  const onSubmit = async (data: MfaChallengeFormData) => {
    await handleContinue(data.code, data.rememberDevice);
  };

  return (
    <div className="overflow-hidden rounded-none bg-white shadow-[0_30px_80px_rgba(2,6,23,0.35)] min-h-screen">
      <div className="grid min-h-screen grid-cols-1 md:grid-cols-2">
        <div className="relative order-1 h-56 sm:h-72 md:order-2 md:block md:h-full">
          <img
            src={heroImageUrl}
            alt="Auth0 experience preview"
            className="absolute inset-0 h-full w-full object-cover"
            loading="lazy"
          />
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-slate-900/10 via-transparent to-slate-900/30" />
        </div>

        <form
          className="flex h-full flex-col justify-center gap-6 order-2 bg-white px-10 py-12 md:order-1 md:px-12"
          onSubmit={handleSubmit(onSubmit)}
        >
          <div className="mb-0 flex flex-col items-start text-left">
            <img
              src={butterfieldLogo}
              alt="Butterfield logo"
              className="h-14 w-auto"
              loading="lazy"
            />
            <p className="mt-4 font-display text-xl font-normal text-ink">
              {headingText}
            </p>
            <p className="mt-2 text-sm text-ink-2">{descriptionText}</p>
          </div>

          {generalErrors.length > 0 && (
            <div className="rounded-none border border-danger/30 bg-danger-bg px-4 py-3 text-sm text-danger">
              <ul className="list-disc space-y-1 pl-5">
                {generalErrors.map((error, index) => (
                  <li key={`mfa-challenge-error-${index}`}>{error.message}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="space-y-2">
            <label
              className="text-sm font-semibold text-ink-2"
              htmlFor="code"
            >
              One-Time Code*
            </label>
            <input
              id="code"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder={codePlaceholder}
              className="input-field tracking-[0.3em]"
              aria-invalid={
                !!formErrors.code?.message || !!codeSDKError || undefined
              }
              {...register("code", {
                required: "Please enter the code",
                minLength: {
                  value: 6,
                  message: "Code must be 6 digits",
                },
                maxLength: {
                  value: 8,
                  message: "Code is too long",
                },
              })}
            />
            {(formErrors.code?.message || codeSDKError) && (
              <p className="text-sm text-danger">
                {formErrors.code?.message || codeSDKError}
              </p>
            )}
          </div>

          {showRememberDevice && (
            <div className="flex items-center gap-3">
              <input
                id="rememberDevice"
                type="checkbox"
                className="h-4 w-4 border-border accent-accent"
                {...register("rememberDevice")}
              />
              <label
                htmlFor="rememberDevice"
                className="text-sm text-ink-2"
              >
                {rememberMeText}
              </label>
            </div>
          )}

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full rounded-sm border border-accent bg-accent px-4 py-3 text-base font-semibold uppercase tracking-[0.1em] text-on-accent transition hover:cursor-pointer hover:border-accent-hover hover:bg-accent-hover hover:shadow-sm disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isSubmitting ? "Please wait..." : buttonText}
          </button>

          <button
            type="button"
            onClick={handleTryAnotherMethod}
            className="text-sm font-semibold text-ink-2 underline underline-offset-2 hover:cursor-pointer hover:text-ink"
          >
            {tryAnotherMethodText}
          </button>
        </form>
      </div>
    </div>
  );
}

export default MfaChallengeScreen;
